// analyticsService.global.js
(function () {
  class AnalyticsService {
    constructor(cfg = {}) {
      this.IMP_URL   = cfg.impressionUrl  || '/analytics/impression';
      this.INTER_URL = cfg.interactionUrl || '/analytics/interaction';
      this.IDLE_MS   = Number(cfg.idleMs ?? 15000);
      this.currency  = cfg.currency || (window?.theme?.currency || 'USD');
      this.currentSearchId = null;
      this._impressionObserver = null;
      this._impressionsSent = new Set();
      this._elToPage = new Map();
      this._productResultsContainer = null;
      this._productResultsLimit = 0;
      this._productItemSelector = '';
      this._loadingClass = '';
      this._sentKeys = new Set();
      this._dwell = new Map();
      this._leaveBound = false;
      this._boundBumpHandlers = new Map();
      this._teardownFns = [];
    }

    setSearchId(id) { this.currentSearchId = id || null; }
    
    getSearchId() { return this.currentSearchId }

    configureImpressions({ container, limit, productItemSelector, loadingClass }) {
      this._productResultsContainer = container;
      this._productResultsLimit = limit;
      this._productItemSelector = productItemSelector;
      this._loadingClass = loadingClass;
      if (this._impressionObserver) { this._impressionObserver.disconnect(); this._impressionObserver = null; }
    }
    updateCurrency(c){ this.currency = c || this.currency; }

    _initImpressionObserver() {
      if (this._impressionObserver) return;
      const root = this._productResultsContainer || null;
      this._impressionObserver = new IntersectionObserver((entries)=>{
        entries.forEach((entry)=>{
          const el = entry.target;
          const page = this._elToPage.get(el);
          if (page == null || this._impressionsSent.has(page)) {
            this._impressionObserver.unobserve(el); return;
          }
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this._sendImpression(page); this._impressionObserver.unobserve(el);
          }
        });
      }, { root, threshold: 0.5 });
      this._teardownFns.push(()=> this._impressionObserver?.disconnect());
    }

    observePageByItem(page){
      if (this._impressionsSent.has(page)) return;
      const startIndex = page * this._productResultsLimit;
      const container = this._productResultsContainer || document;
      const items = container.querySelectorAll(`${this._productItemSelector}:not(.${this._loadingClass})`);
      const el = items[startIndex];
      if (!el) { requestAnimationFrame(()=> this.observePageByItem(page)); return; }
      this._initImpressionObserver(); this._elToPage.set(el, page); this._impressionObserver.observe(el);
    }

    // Add this method to AnalyticsService
    observeItemsInPage(page) {
      if (!this.currentSearchId) return;

      const container = this._productResultsContainer || document;
      const items = Array.from(container.querySelectorAll(
        `${this._productItemSelector}:not(.${this._loadingClass})`
      ));
      const pageStart = page * this._productResultsLimit;
      const pageEndExclusive = pageStart + this._productResultsLimit;

      // Track which indices (within the page) we've already sent
      const seen = new Set();

      const flushRanges = (idxs) => {
        idxs.sort((a,b)=>a-b);
        let s = idxs[0], prev = idxs[0];
        for (let k = 1; k <= idxs.length; k++) {
          const cur = idxs[k];
          if (cur !== prev + 1) {
            this._postJSONBeacon(this.IMP_URL, {
              searchId: this.currentSearchId,
              page,
              visibleRange: { start: s, end: prev } // <-- { start, end }
            });
            s = cur;
          }
          prev = cur;
        }
      };

      const io = new IntersectionObserver((entries) => {
        const newlyVisible = [];
        for (const e of entries) {
          if (!e.isIntersecting || e.intersectionRatio < 0.5) continue;
          const i = items.indexOf(e.target);
          if (i < pageStart || i >= pageEndExclusive) continue; // not this page
          const idxWithinPage = i - pageStart;
          if (!seen.has(idxWithinPage)) {
            seen.add(idxWithinPage);
            newlyVisible.push(idxWithinPage);
          }
        }
        if (newlyVisible.length) flushRanges(newlyVisible);
      }, { root: this._productResultsContainer || null, threshold: 0.5 });

      for (let i = pageStart; i < Math.min(pageEndExclusive, items.length); i++) {
        io.observe(items[i]);
      }

      this._teardownFns.push(() => io.disconnect());
    }

    sendPeekImpression(page, range) {
      this._postJSONBeacon(this.IMP_URL, { searchId: this.currentSearchId, page, visibleRange: { start: range.start, end: range.end} });
    }


    _sendImpression(page){
      if (!this.currentSearchId) return;
      this._impressionsSent.add(page);
      this._postJSONBeacon(this.IMP_URL, { searchId: this.currentSearchId, page });
    }

    _interactionKey(type, handle){ return `${type}:${this.currentSearchId || ''}:${handle}`; }
    _sendInteraction(type, { handle, dwellMs, qty, priceAtAction, currency } = {}){
      if (!handle || !this.currentSearchId) return;
      const key = this._interactionKey(type, handle);
      if (this._sentKeys.has(key) && (type === 'click' || type === 'add_to_cart' || type === 'purchase')) return;
      this._sentKeys.add(key);
      this._postJSONBeacon(this.INTER_URL, {
        searchId: this.currentSearchId, handle, type, dwellMs, qty, priceAtAction, currency: currency || this.currency
      });
    }

    trackClick(handle){ this._sendInteraction('click', { handle }); }
    trackAddToCart(handle, qty=1, priceAtAction){ this._sendInteraction('add_to_cart', { handle, qty, priceAtAction }); }
    trackPurchase(handle, qty=1, priceAtAction){ this._sendInteraction('purchase', { handle, qty, priceAtAction }); }

    _bindLeaveHandlers(){
      if (this._leaveBound) return; this._leaveBound = true;
      const onPageHide = ()=> this._stopAllViews('pagehide');
      const onVis = ()=> { if (document.visibilityState === 'hidden') this._stopAllViews('hidden'); };
      window.addEventListener('pagehide', onPageHide, { passive: true });
      document.addEventListener('visibilitychange', onVis, { passive: true });
      this._teardownFns.push(()=>{
        window.removeEventListener('pagehide', onPageHide);
        document.removeEventListener('visibilitychange', onVis);
        this._leaveBound = false;
      });
    }

    _startIdleWatch(handle){
      const d = this._dwell.get(handle); if (!d) return;
      clearTimeout(d.idleTimer);
      d.idleTimer = setTimeout(()=> this.stopView(handle, 'idle'), this.IDLE_MS);
    }

    startView(handle){
      if (!handle || !this.currentSearchId) return;
      this._bindLeaveHandlers();
      this.stopView(handle, 'restart');
      this._dwell.set(handle, { t0: performance.now(), idleTimer: null });
      const bump = ()=> this._startIdleWatch(handle);
      const events = ['mousemove','keydown','scroll','touchstart'];
      events.forEach(ev=> window.addEventListener(ev, bump, { passive: true }));
      this._boundBumpHandlers.set(handle, ()=> {
        events.forEach(ev=> window.removeEventListener(ev, bump));
      });
      this._startIdleWatch(handle);
    }

    stopView(handle, reason='end'){
      const d = this._dwell.get(handle); if (!d) return;
      clearTimeout(d.idleTimer);
      const dwellMs = Math.max(0, Math.round(performance.now() - d.t0));
      this._dwell.delete(handle);
      const unbind = this._boundBumpHandlers.get(handle);
      if (unbind) { unbind(); this._boundBumpHandlers.delete(handle); }
      this._sendInteraction('view', { handle, dwellMs });
    }

    _stopAllViews(){
      for (const handle of Array.from(this._dwell.keys())) this.stopView(handle, 'end');
    }

    _postJSONBeacon(url, obj){
      const body = JSON.stringify(obj);
      const blob = new Blob([body], { type: 'application/json; charset=UTF-8' });
      if (!navigator.sendBeacon(url, blob)) {
        fetch(url, { method:'POST', headers:{ 'Content-Type':'application/json' }, credentials:'include', keepalive:true, body })
          .catch(()=>{});
      }
    }

    resetImpressions(){
      this._impressionsSent.clear(); this._elToPage.clear();
      if (this._impressionObserver) { this._impressionObserver.disconnect(); this._impressionObserver = null; }
    }

    destroy(){
      this._stopAllViews('destroy'); this.resetImpressions(); this._sentKeys.clear();
      this._boundBumpHandlers.forEach(fn=> fn()); this._boundBumpHandlers.clear();
      this._teardownFns.forEach(fn=> { try{ fn(); }catch{} }); this._teardownFns = [];
    }
  }

  // expose globally
  window.AnalyticsService = AnalyticsService;
})();
